import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, PenLine } from "lucide-react";
import usePageMeta from "../hooks/usePageMeta";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import FaqSection from "../components/FaqSection";
import GuidebookCard from "../components/GuidebookCard";
import StargatePortal from "../components/StargatePortal";
import { SHOP_URL, IMAGES, RV_STAGES, RV_FAQ, GUIDEBOOKS } from "../data/content";

function Overview() {
  return (
    <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:px-10 lg:py-32">
      <div className="grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <Reveal>
            <h2 className="font-display text-3xl font-light leading-tight tracking-tight sm:text-4xl">
              What it is,
              <br />
              in plain terms.
            </h2>
          </Reveal>
        </div>
        <div className="lg:col-span-7 lg:col-start-6">
          <Reveal>
            <p className="body-muted text-base sm:text-lg">
              Remote viewing is a structured protocol for acquiring impressions
              of a target — a place, object, or event — hidden from ordinary
              perception. It was researched for decades in programs associated
              with Stanford Research Institute and Project Stargate, and refined
              into a repeatable written procedure.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="body-muted mt-6 text-sm sm:text-base">
              You are given only a set of random coordinates. From there, the
              work is almost anticlimactic: spontaneous ink lines, fragments of
              sensation, sketches of proportion. The discipline lies in
              recording what arrives before the mind names it — and in verifying
              everything afterwards against feedback.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-10 border border-white/[0.08] bg-[#0F121B]/70 p-7 sm:p-8">
              <p className="flex items-center gap-3 text-[11px] font-mono-ui uppercase tracking-[0.22em] text-[#A78BFA]">
                <PenLine className="h-4 w-4" /> What a session needs
              </p>
              <ul className="mt-6 grid gap-3 text-sm text-[#8E95A5] sm:grid-cols-2">
                <li className="flex gap-3"><span className="text-[#38BDF8]">—</span>A quiet room and 30–45 minutes</li>
                <li className="flex gap-3"><span className="text-[#38BDF8]">—</span>Pen and unlined paper</li>
                <li className="flex gap-3"><span className="text-[#38BDF8]">—</span>A blind target coordinate</li>
                <li className="flex gap-3"><span className="text-[#38BDF8]">—</span>A session log for honest feedback</li>
              </ul>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Stages() {
  return (
    <section className="relative overflow-hidden border-t border-white/[0.06] bg-[#0C0E14]/40 py-24 lg:py-32" data-testid="rv-stages-section">
      <StargatePortal
        size={520}
        className="absolute -right-56 top-24 hidden opacity-25 lg:block"
      />
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-10">
        <Reveal>
          <p className="eyebrow">The Protocol</p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-display mt-6 max-w-2xl text-3xl font-light leading-[1.12] tracking-tight sm:text-4xl lg:text-5xl">
            The six-phase standard.
          </h2>
        </Reveal>
        <div className="mt-16 border-t border-white/[0.07]">
          {RV_STAGES.map((s, i) => (
            <Reveal key={s.stage} delay={0.06 * i}>
              <div className="group grid gap-3 border-b border-white/[0.07] py-8 transition-colors duration-300 hover:bg-white/[0.02] sm:grid-cols-12 sm:gap-6 sm:px-4">
                <p className="font-mono-ui text-[11px] tracking-[0.22em] text-[#38BDF8] sm:col-span-2 sm:pt-2">
                  {s.stage.toUpperCase()}
                </p>
                <h3 className="font-display text-2xl text-[#F0F2F8] transition-colors duration-300 group-hover:text-[#A78BFA] sm:col-span-4">
                  {s.name}
                </h3>
                <p className="body-muted text-sm leading-relaxed sm:col-span-6">
                  {s.detail}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function GuidebookPathways() {
  const books = GUIDEBOOKS.filter((b) => b.category === "Remote Viewing");
  return (
    <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:px-10 lg:py-32">
      <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
        <div>
          <Reveal>
            <p className="eyebrow">Guidebook Pathways</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display mt-6 max-w-2xl text-3xl font-light leading-[1.12] tracking-tight sm:text-4xl lg:text-5xl">
              Manuals for the coordinate discipline.
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.2}>
          <p className="body-muted max-w-sm text-sm leading-relaxed">
            Field manuals formatted with session worksheets, target coordinate
            archives, and step-by-step drills.
          </p>
        </Reveal>
      </div>
      <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {books.map((book, i) => (
          <Reveal key={book.id} delay={0.1 * i} className="h-full">
            <GuidebookCard book={book} />
          </Reveal>
        ))}
        <Reveal delay={0.25} className="h-full">
          <Link
            to="/guidebooks"
            data-testid="rv-all-guidebooks-btn"
            className="group flex h-full min-h-[220px] flex-col justify-between border border-dashed border-white/15 p-8 transition-all duration-300 hover:border-[#8B5CF6]/60"
          >
            <p className="font-display text-2xl text-[#F0F2F8]">
              See the full
              <br />
              library.
            </p>
            <span className="inline-flex items-center gap-2 font-mono-ui text-[11px] uppercase tracking-[0.22em] text-[#A78BFA]">
              All guidebooks
              <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1.5" />
            </span>
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function ContinueLearning() {
  return (
    <section className="relative overflow-hidden border-t border-white/[0.07]">
      <div className="mx-auto grid max-w-7xl gap-px bg-white/[0.07] sm:grid-cols-2 lg:grid-cols-3">
        <Link
          to="/astral-projection"
          data-testid="rv-continue-astral-btn"
          className="group bg-[#0A0C12] p-10 transition-colors duration-500 hover:bg-[#0F121B] lg:p-12"
        >
          <p className="eyebrow">Next Discipline</p>
          <p className="font-display mt-5 text-2xl text-[#F0F2F8] transition-colors duration-300 group-hover:text-[#A78BFA] sm:text-3xl">
            Continue to Astral Projection
          </p>
          <span className="mt-6 inline-flex items-center gap-2 font-mono-ui text-[11px] uppercase tracking-[0.22em] text-[#38BDF8]">
            Cross the threshold
            <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1.5" />
          </span>
        </Link>
        <Link
          to="/faq"
          data-testid="rv-continue-faq-btn"
          className="group bg-[#0A0C12] p-10 transition-colors duration-500 hover:bg-[#0F121B] lg:p-12"
        >
          <p className="eyebrow">Before You Begin</p>
          <p className="font-display mt-5 text-2xl text-[#F0F2F8] transition-colors duration-300 group-hover:text-[#A78BFA] sm:text-3xl">
            Read the common inquiries
          </p>
          <span className="mt-6 inline-flex items-center gap-2 font-mono-ui text-[11px] uppercase tracking-[0.22em] text-[#38BDF8]">
            Open the FAQ
            <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1.5" />
          </span>
        </Link>
        <a
          href={SHOP_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="rv-continue-shop-btn"
          className="group bg-[#0A0C12] p-10 transition-colors duration-500 hover:bg-[#0F121B] lg:p-12"
        >
          <p className="eyebrow">The Storefront</p>
          <p className="font-display mt-5 text-2xl text-[#F0F2F8] transition-colors duration-300 group-hover:text-[#A78BFA] sm:text-3xl">
            Get the manual, start tonight
          </p>
          <span className="mt-6 inline-flex items-center gap-2 font-mono-ui text-[11px] uppercase tracking-[0.22em] text-[#38BDF8]">
            Fourthwall Shop <ArrowUpRight className="h-3.5 w-3.5" />
          </span>
        </a>
      </div>
    </section>
  );
}

export default function RemoteViewing() {
  usePageMeta(
    "Remote Viewing — The Coordinate Discipline | Consciousness Ascending",
    "Learn Controlled Remote Viewing: the six-phase protocol, beginner guidance, and field manual guidebooks. Purchases via our official Fourthwall shop."
  );
  return (
    <>
      <PageHero
        eyebrow="Discipline 01 // Remote Viewing"
        lines={["Perceive beyond", "distance & time."]}
        description="Originating in government-funded research at Stanford Research Institute (SRI), Remote Viewing is a standardized procedure to sketch, describe, and decode hidden targets across time and distance — with nothing but pen, paper, and protocol."
        image={IMAGES.rv}
        imageAlt="Field Manual // The coordinate discipline"
        meta="PROTOCOL: CRV // STAGES I–VI // PEN & PAPER ONLY"
      />
      <Overview />
      <Stages />
      <GuidebookPathways />
      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:px-10 lg:py-32">
        <Reveal>
          <p className="eyebrow">Inquiries</p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-display mt-6 text-3xl font-light tracking-tight sm:text-4xl">
            Remote viewing, questioned.
          </h2>
        </Reveal>
        <Reveal delay={0.2} className="mt-12">
          <FaqSection items={RV_FAQ} />
        </Reveal>
      </section>
      <ContinueLearning />
    </>
  );
}
